import type { Booking, BookingsMap, ConflictMap, SlotData } from "../../hooks/bookingTypes";
import type { GridQuestRow } from "../../hooks/useGridData";
import { presetTimeOf } from "../../hooks/shiftedSlots";
import { Slot } from "./Slot";
import css from "./BookingGrid.module.css";

/**
 * Booking grid (B.2.4). Sticky left column with quest short names; each
 * quest row carries its OWN slot times (no shared header). No horizontal
 * scroll — slots shrink to fit; no now-line.
 */
export interface BookingGridProps {
  rows: GridQuestRow[];
  bookings: BookingsMap;
  conflicts: ConflictMap;
  moveMode: boolean;
  /** Booking being moved — used to highlight its origin cell. */
  movingId: number | null;
  /** Ids of just-appeared bookings — those cells play an entrance animation. */
  newIds?: Set<number>;
  onSlotClick: (questId: string, baseTime: string, data: SlotData | null, conflict: Booking | null, presetTime?: string | null, clash?: string | null) => void;
  /** Тап по названию квеста — открыть его многодневное расписание. */
  onQuestClick?: (questId: string) => void;
}

export function BookingGrid({ rows, bookings, conflicts, moveMode, movingId, newIds, onSlotClick, onQuestClick }: BookingGridProps) {
  if (rows.length === 0) {
    return <div className={css.empty}>Нет активных квестов в выбранной зоне</div>;
  }
  return (
    <div className={css.wrap} role="grid">
      {rows.map((row) => (
        <div key={row.questId} className={css.qRow} style={{ ["--n" as never]: row.slots.length }}>
          {onQuestClick ? (
            <div
              className={`${css.qName} ${css.qNameBtn}`}
              title={`${row.questName} — расписание`}
              role="button"
              tabIndex={0}
              onClick={() => onQuestClick(row.questId)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onQuestClick(row.questId);
                }
              }}
            >
              {row.shortName}
            </div>
          ) : (
            <div className={css.qName} title={row.questName}>
              {row.shortName}
            </div>
          )}
          <div className={css.qSlots}>
            {row.slots.map((slot) => {
              const key = `${row.questId}_${slot.base}`;
              const data: SlotData | null = bookings[key] ?? null;
              const conflict = conflicts[key] ?? null;
              /* A48-коррекция 13.08: свободные слоты в move-режиме НЕ подсвечиваются
                 (сетка выглядит как обычно) — маркируется только исходная бронь. */
              const isMoveTarget = false;
              const isMovingOrigin =
                moveMode &&
                data &&
                data.type !== "auto-block" &&
                (data as Booking).id === movingId;
              const isNew =
                !!newIds &&
                !!data &&
                data.type !== "auto-block" &&
                newIds.has((data as Booking).id);
              return (
                <Slot
                  key={slot.base}
                  questId={row.questId}
                  displayTime={slot.disp}
                  baseTime={slot.base}
                  data={data}
                  conflictLead={conflict}
                  moveTarget={isMoveTarget}
                  focused={isMovingOrigin || undefined}
                  isNew={isNew}
                  clash={slot.clash ?? null}
                  presetTime={presetTimeOf(slot, moveMode ? movingId : null)}   /* 04.10: сдвиг вперёд (МК, лишняя чайная) — бронь на сдвинутое время; ревью: назад и «свой» сдвиг в переносе — базовое */
                  onClick={onSlotClick}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
